import {
	ConfirmationDialog,
	ContextMenu,
	ContextMenuItem,
	ContextMenuLinkItem,
	IDataListColumn,
	IconButton,
	Id,
	Loading,
	PaginatedAutoDataList,
	PaginatedListFetcherConfig,
	isSuccess,
	laravelPaginatedListFetchConfig,
	useToasts,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ContactFilterApplyDrawer, ContactFilterBar } from "../../../contactFilter";
import { useContactFilter } from "../../../contactFilter/useContactFilter";
import { useContactListUtils } from "../../../contacts/useContactListUtils";
import { LocalContactViewSwitcher } from "../../../contactViews/local/LocalContactViewSwitcher";
import { contactUpdateInquiryService } from "../../../shared/api/contactUpdateInquiryService";
import { useContactView } from "../../../shared/hooks/useContactView";
import { IContactIndexResource } from "../../../shared/types";
import { ContactUpdateInquiryContactIndexResource } from "../../../shared/types/contactUpdateInquiry";
import { OpenDeletionInquiryBulkAction } from "./OpenDeletionInquiryBulkAction";

interface Props {
	contactUpdateInquiryId: Id;
}

export const OpenDeletionInquiryContactList: FC<Props> = ({ contactUpdateInquiryId }) => {
	const { t } = useTranslation();
	const { currentContactView, isLoading: contactViewLoading } = useContactView();
	const { columns } = useContactListUtils(currentContactView);
	const { showToast } = useToasts();
	const queryClient = useQueryClient();
	const { onApplyContactFilter, openContactFilterDrawer, activeContactFilterId, applyFilterDrawerProps } =
		useContactFilter(contactUpdateInquiryService.contactsWithOpenDeletionInquiryKey(contactUpdateInquiryId));
	const [dialogProps, setDialogProps] = useState<{
		contactId: Id;
		isAccepted: boolean;
	} | null>(null);

	const queryKey = useMemo(() => {
		const baseKey = [
			...contactUpdateInquiryService.contactsWithOpenDeletionInquiryKey(contactUpdateInquiryId),
			contactUpdateInquiryService.contactsWithInquiryKeySuffix,
			`${activeContactFilterId}`,
		];

		if (currentContactView?.id) {
			return baseKey.concat(`${currentContactView.id}`);
		}

		return baseKey;
	}, [activeContactFilterId, contactUpdateInquiryId, currentContactView]);

	const acceptDeletionInquiry = useCallback(
		async (inquiryId: Id, contactId: Id, acceptInquiry: boolean) => {
			const response = await contactUpdateInquiryService.acceptDeletionInquiry(inquiryId, contactId, acceptInquiry);

			if (isSuccess(response)) {
				if (acceptInquiry) {
					showToast({
						body: t("The deletion inquiry was accepted."),
						title: t("Deletion inquiry"),
						type: "success",
					});
				} else {
					showToast({
						body: t("The deletion inquiry was declined."),
						title: t("Deletion inquiry"),
						type: "success",
					});
				}
				await queryClient.invalidateQueries({
					queryKey: contactUpdateInquiryService.contactsWithOpenDeletionInquiryKey(inquiryId),
				});
				await queryClient.invalidateQueries({ queryKey: contactUpdateInquiryService.detailsKey(inquiryId) });
				await queryClient.invalidateQueries({
					queryKey: contactUpdateInquiryService.contactsWithCompletedInquiryKey(inquiryId),
				});
			}
		},
		[queryClient, showToast, t],
	);

	const dataListColumns: IDataListColumn<IContactIndexResource>[] = useMemo(() => {
		return [
			...columns,
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: (contact: IContactIndexResource) => (
					<ContextMenu displayShortcutsAs="icon">
						<ContextMenuItem
							label={t("Accept")}
							action={() => setDialogProps({ contactId: contact.id, isAccepted: true })}
							title={t("Accept")}
							icon="check"
							shortcut="primary"
						/>
						<ContextMenuItem
							label={t("Decline")}
							action={() => setDialogProps({ contactId: contact.id, isAccepted: false })}
							title={t("Decline")}
							icon="close"
							shortcut={true}
						/>
						<ContextMenuLinkItem
							icon="eye"
							to={`/contacts/active/${contact.id}`}
							label={t("View contact")}
							title={t("View contact")}
						/>
					</ContextMenu>
				),
			},
		];
	}, [columns, t]);

	const initialSortingAttribute =
		currentContactView?.contactAttributes.find((attribute) => attribute.entityType === "lastName") ??
		currentContactView?.contactAttributes.find((attribute) => attribute.entityType === "createdAt");

	if (contactViewLoading) {
		return <Loading />;
	}

	return (
		<>
			<PaginatedAutoDataList
				name="deletionInquiries"
				empty={t("No inquiries available.")}
				headerChildren={
					<ContactFilterBar
						contactFilterId={activeContactFilterId}
						onApply={onApplyContactFilter}
						onClick={openContactFilterDrawer}
					/>
				}
				heading={t("Deletion inquiries")}
				columns={dataListColumns}
				listFetchConfig={laravelPaginatedListFetchConfig(
					queryKey,
					(config: PaginatedListFetcherConfig<ContactUpdateInquiryContactIndexResource>) =>
						contactUpdateInquiryService.contactsWithOpenDeletionInquiry(
							config,
							activeContactFilterId,
							currentContactView?.id || null,
							contactUpdateInquiryId,
						),
				)}
				headerActions={
					<>
						<LocalContactViewSwitcher />
						<IconButton size="small" title={t("Contact filter")} icon="filter" onClick={openContactFilterDrawer} />
					</>
				}
				initialSearch={{
					search: "",
					searchFields: [
						{ name: "firstName", label: t("First name") },
						{ name: "lastName", label: t("Last name") },
						{ name: "email", label: t("Email") },
						{ name: "phone", label: t("Phone") },
						{ name: "company", label: t("Company") },
					],
				}}
				onRowClick={({ id }) => setDialogProps({ contactId: id, isAccepted: true })}
				enableBulkEdit={true}
				bulkEditActions={
					<OpenDeletionInquiryBulkAction
						contactUpdateInquiryId={contactUpdateInquiryId}
						filterId={activeContactFilterId}
					/>
				}
				initialSorting={
					initialSortingAttribute ? { column: initialSortingAttribute.id as any, direction: "asc" } : null
				}
			/>
			{applyFilterDrawerProps.editEntityId != null && (
				<ContactFilterApplyDrawer
					{...applyFilterDrawerProps}
					contactFilterId={activeContactFilterId}
					onApply={onApplyContactFilter}
				/>
			)}
			<ConfirmationDialog
				open={!!dialogProps}
				onClose={() => setDialogProps(null)}
				action={() => {
					dialogProps && acceptDeletionInquiry(contactUpdateInquiryId, dialogProps.contactId, dialogProps.isAccepted);
					setDialogProps(null);
				}}
				heading={t("DELETION_INQUIRY")}
				labels={{ close: t("Close"), confirm: t("Confirm") }}
			>
				{dialogProps?.isAccepted
					? t("Are you sure you want to accept this deletion inquiry?")
					: t("Are you sure you want to decline this deletion inquiry?")}
			</ConfirmationDialog>
		</>
	);
};
